import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { CategoryMaster } from '../../types/master';

interface Props {
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
}

const CategoryMasterTab: React.FC<Props> = ({ onError, onSuccess }) => {
  const [categories, setCategories] = useState<CategoryMaster[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CategoryMaster | null>(null);
  const [formData, setFormData] = useState({
    code: '',
    nameJa: '',
    nameTh: '',
    nameEn: '',
    icon: '',
    sortOrder: 0,
    isActive: true,
  });

  const functions = getFunctions();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const getMasterData = httpsCallable(functions, 'getMasterData');
      const result = await getMasterData({ type: 'categories', includeInactive: true });
      const data = (result.data as any).data as CategoryMaster[];
      setCategories(data);
    } catch (error: any) {
      onError(error.message || '業種マスタの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (category?: CategoryMaster) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        code: category.code,
        nameJa: category.name.ja,
        nameTh: category.name.th || '',
        nameEn: category.name.en || '',
        icon: category.icon,
        sortOrder: category.sortOrder,
        isActive: category.isActive,
      });
    } else {
      setEditingCategory(null);
      setFormData({
        code: '',
        nameJa: '',
        nameTh: '',
        nameEn: '',
        icon: '',
        sortOrder: categories.length + 1,
        isActive: true,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCategory(null);
  };

  const handleSave = async () => {
    try {
      if (!formData.code || !formData.nameJa || !formData.icon) {
        onError('必須項目を入力してください');
        return;
      }

      if (editingCategory) {
        // 更新
        const updateMasterData = httpsCallable(functions, 'updateMasterData');
        await updateMasterData({
          id: editingCategory.id,
          type: 'categories',
          data: {
            code: formData.code,
            name: {
              ja: formData.nameJa,
              th: formData.nameTh || undefined,
              en: formData.nameEn || undefined,
            },
            icon: formData.icon,
            sortOrder: formData.sortOrder,
            isActive: formData.isActive,
          },
        });
        onSuccess('業種マスタを更新しました');
      } else {
        // 新規作成
        const createCategory = httpsCallable(functions, 'createCategory');
        await createCategory({
          code: formData.code,
          name: {
            ja: formData.nameJa,
            th: formData.nameTh || undefined,
            en: formData.nameEn || undefined,
          },
          icon: formData.icon,
          sortOrder: formData.sortOrder,
          isActive: formData.isActive,
        });
        onSuccess('業種マスタを作成しました');
      }

      handleCloseDialog();
      fetchCategories();
    } catch (error: any) {
      onError(error.message || '保存に失敗しました');
    }
  };

  const handleDelete = async (category: CategoryMaster) => {
    if (!window.confirm(`${category.name.ja}を削除してもよろしいですか？`)) {
      return;
    }

    try {
      const deleteMasterData = httpsCallable(functions, 'deleteMasterData');
      const result = await deleteMasterData({
        id: category.id,
        type: 'categories',
      });
      onSuccess((result.data as any).message || '業種マスタを削除しました');
      fetchCategories();
    } catch (error: any) {
      onError(error.message || '削除に失敗しました');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          業種を追加
        </Button>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>アイコン</TableCell>
              <TableCell>コード</TableCell>
              <TableCell>名称（日本語）</TableCell>
              <TableCell>名称（タイ語）</TableCell>
              <TableCell>名称（英語）</TableCell>
              <TableCell>表示順</TableCell>
              <TableCell>状態</TableCell>
              <TableCell align="right">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.map((category) => (
              <TableRow key={category.id}>
                <TableCell style={{ fontSize: '2rem' }}>{category.icon}</TableCell>
                <TableCell>{category.code}</TableCell>
                <TableCell>{category.name.ja}</TableCell>
                <TableCell>{category.name.th || '-'}</TableCell>
                <TableCell>{category.name.en || '-'}</TableCell>
                <TableCell>{category.sortOrder}</TableCell>
                <TableCell>
                  <Chip
                    label={category.isActive ? '有効' : '無効'}
                    color={category.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleOpenDialog(category)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(category)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCategory ? '業種マスタを編集' : '業種マスタを追加'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="コード"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            margin="normal"
            required
            disabled={!!editingCategory}
          />
          <TextField
            fullWidth
            label="名称（日本語）"
            value={formData.nameJa}
            onChange={(e) => setFormData({ ...formData, nameJa: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="名称（タイ語）"
            value={formData.nameTh}
            onChange={(e) => setFormData({ ...formData, nameTh: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="名称（英語）"
            value={formData.nameEn}
            onChange={(e) => setFormData({ ...formData, nameEn: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="アイコン"
            value={formData.icon}
            onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
            margin="normal"
            required
            placeholder="🏥"
            helperText="絵文字またはURL"
          />
          <TextField
            fullWidth
            label="表示順"
            type="number"
            value={formData.sortOrder}
            onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) })}
            margin="normal"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
            }
            label="有効"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button onClick={handleSave} variant="contained">
            保存
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CategoryMasterTab;
